import React, { useState, useEffect } from 'react';
import { Download, Copy, CheckCheck, Search, MapPin, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/axios';
import './QRGenerator.css';

const QRGenerator = () => {
  const [searchBinId, setSearchBinId] = useState('');
  const [availableBins, setAvailableBins] = useState([]);
  const [selectedBin, setSelectedBin] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingQR, setGeneratingQR] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  // Add error boundary for this component
  const [componentError, setComponentError] = useState(null);
  const [error, setError] = useState(null);

  // Wrap all operations in try-catch
  const safeExecute = (fn, context = 'Unknown') => {
    try {
      return fn();
    } catch (error) {
      console.error(`❌ Error in ${context}:`, error);
      setComponentError(error);
      toast.error(`Error in ${context}: ${error.message}`);
      return null;
    }
  };

  // Fetch all bins on component mount
  useEffect(() => {
    fetchBins();
  }, []);

  // Debug QR code data changes
  useEffect(() => {
    if (qrCodeData) {
      console.log('🔍 QR Code Data Updated:', qrCodeData);
      console.log('📝 QR Code URL:', qrCodeData.qrCode?.url);
      console.log('🖼️ QR Code Data URL:', qrCodeData.qrCode?.dataURL);
    }
  }, [qrCodeData]);

  const fetchBins = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bins');
      
      if (response.data && Array.isArray(response.data)) {
        setAvailableBins(response.data);
        console.log('✅ Bins loaded successfully:', response.data.length);
        console.log('📋 First bin sample:', response.data[0]);
      } else {
        console.warn('⚠️ Unexpected bins data format:', response.data);
        setAvailableBins([]);
      }
    } catch (error) {
      console.error('❌ Error fetching bins:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch bins';
      toast.error(errorMessage);
      setAvailableBins([]);
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async (binId) => {
    try {
      setGeneratingQR(true);
      setError(null);
      
      // Get the token from localStorage
      const token = localStorage.getItem('binbuddy_token');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      // Find the bin data
      const bin = availableBins.find(b => b.binId === binId);
      if (!bin) {
        throw new Error('Bin data not found');
      }

      console.log('🔍 Fetching QR code for bin:', binId);
      
      // Make the API request to generate QR code
      const response = await api.get(`/bins/generate-qr/${binId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📦 QR Code API Response:', response.data);

      if (response.data.success) {
        // Create the public URL for this bin
        const publicUrl = `${window.location.origin}/report-bin/${binId}`;
        
        const mergedBinData = response.data.binData 
          ? { ...bin, ...response.data.binData }
          : bin;

        // Use the full qrCode object from the backend
        const qrData = {
          binData: mergedBinData,
          qrCode: {
            ...response.data.qrCode,
            // Ensure we have both dataURL and url
            dataURL: response.data.qrCode?.dataURL || 
                    `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(publicUrl)}`,
            url: response.data.qrCode?.url || publicUrl
          }
        };
        
        console.log('🔗 Generated Public URL:', publicUrl);
        
        console.log('📊 Final QR Data:', qrData);
        
        setQrCodeData(qrData);
        setSelectedBin(mergedBinData);
        setShowQRModal(true);
        toast.success('QR Code generated successfully!');
      } else {
        const errorMsg = response.data?.message || 'Failed to generate QR code';
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('❌ Error generating QR code:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate QR code';
      setError(error);
      toast.error(`QR Generation Error: ${errorMessage}`);
      
      // Set a timeout to clear the error after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setGeneratingQR(false);
    }
  };

  // Filter bins based on search term
  const filteredBins = availableBins.filter(bin =>
    bin.binId.toLowerCase().includes(searchBinId.toLowerCase()) ||
    bin.address?.streetAddress?.toLowerCase().includes(searchBinId.toLowerCase())
  );

  const downloadQRCode = () => {
    try {
      if (!qrCodeData?.qrCode?.dataURL) {
        console.error('No QR code data available for download');
        toast.error('No QR code data available for download');
        return;
      }
      
      const link = document.createElement('a');
      const binId = selectedBin?.binId || qrCodeData?.binData?.binId || 'bin';
      
      link.download = `${binId}-qr-code.png`;
      link.href = qrCodeData.qrCode.dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('QR Code downloaded successfully!');
    } catch (error) {
      console.error('Error downloading QR code:', error);
      toast.error('Failed to download QR code');
    }
  };

  const closeQRModal = () => {
    setShowQRModal(false);
    setQrCodeData(null);
    setSelectedBin(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Early return if component has an error
  if (componentError) {
    return (
      <div className="qr-container">
        <div className="error-state">
          <h3>QR Generator Error</h3>
          <p>Error: {componentError.message}</p>
          <button onClick={() => {
            setComponentError(null);
            window.location.reload();
          }}>
            Reload Component
          </button>
        </div>
      </div>
    );
  }

  return safeExecute(() => (
    <div className="qr-generator-container">
      {/* Header */}
      <div className="qr-header">
        <h1>QR Code Generator</h1>
        <p>Generate QR codes for waste bins to enable public reporting</p>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <div className="search-input-container">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by Bin ID or location..."
            value={searchBinId}
            onChange={(e) => setSearchBinId(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Bins Container */}
      <div className="bins-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading bins...</p>
          </div>
        ) : filteredBins.length > 0 ? (
          <div className="bins-grid">
            {filteredBins.map((bin) => (
              <div key={bin.id || bin._id || bin.binId} className="bin-card">
                <div className="bin-card-header">
                  <h3 className="bin-id">{bin.binId}</h3>
                  <span className={`status-badge status-${bin.status}`}>
                    {bin.status}
                  </span>
                </div>
                
                <div className="bin-card-body">
                  <div className="bin-location">
                    <MapPin size={16} />
                    <span>{bin.address?.streetAddress || 'Unknown location'}</span>
                  </div>
                  
                  <div className="bin-details">
                    <div className="detail-item">
                      <span className="label">Capacity:</span>
                      <span className="value">{bin.capacity}L</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Type:</span>
                      <span className="value">
                        {Array.isArray(bin.type) ? bin.type.join(', ') : bin.type}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">City:</span>
                      <span className="value">{bin.city || 'Unknown'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Zone:</span>
                      <span className="value">{bin.zone || 'Unknown'}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bin-card-footer">
                  <button 
                    onClick={() => {
                      console.log('🖱️ Button clicked for bin:', bin.binId, 'Full bin object:', bin);
                      generateQRCode(bin.binId);
                    }}
                    disabled={generatingQR}
                    className="generate-qr-btn"
                  >
                    {generatingQR ? (
                      <>
                        <div className="btn-spinner"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Search size={16} />
                        Show QR Code
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Trash2 size={48} />
            <h3>No bins found</h3>
            <p>{searchBinId ? 'Try a different search term.' : 'No bins available in the system.'}</p>
          </div>
        )}
      </div>

      {/* QR Code Modal */}
      {showQRModal && qrCodeData && (
        <div className="qr-modal-overlay" onClick={closeQRModal}>
          <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="qr-modal-header">
              <h3>QR Code for {selectedBin?.binId}</h3>
              <button className="close-btn" onClick={closeQRModal}>
                ×
              </button>
            </div>
            
            <div className="qr-modal-body">
              <div className="qr-image-container">
                <img 
                  src={qrCodeData.qrCode.dataURL} 
                  alt={`QR Code for ${selectedBin?.binId}`}
                  className="qr-image"
                />
              </div>
              
              <div className="bin-info-section">
                <h4>Bin Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Bin ID:</span>
                    <span className="value">{selectedBin?.binId}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Location:</span>
                    <span className="value">{selectedBin?.address?.streetAddress || 'Unknown'}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Capacity:</span>
                    <span className="value">{selectedBin?.capacity}L</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Type:</span>
                    <span className="value">
                      {Array.isArray(selectedBin?.type) ? selectedBin.type.join(', ') : selectedBin?.type}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Status:</span>
                    <span className={`value status-${selectedBin?.status}`}>
                      {selectedBin?.status}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="url-section">
                <label>QR Code URL:</label>
                <div className="url-display">
                  <input 
                    type="text" 
                    value={qrCodeData.qrCode.url || qrCodeData.qrCode.dataURL} 
                    readOnly 
                    className="url-input"
                    placeholder="Loading URL..."
                  />
                  <button 
                    onClick={() => copyToClipboard(qrCodeData.qrCode.url || qrCodeData.qrCode.dataURL)}
                    className="copy-btn"
                    title="Copy to clipboard"
                  >
                    <Copy size={16} />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="qr-modal-footer">
              <button onClick={downloadQRCode} className="download-btn">
                <Download size={16} />
                Download QR Code
              </button>
              <button onClick={closeQRModal} className="cancel-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="instructions-section">
        <h3>How to Use QR Codes</h3>
        <div className="instructions-grid">
          <div className="instruction-item">
            <CheckCheck size={20} />
            <span>QR codes are generated fresh with current bin data</span>
          </div>
          <div className="instruction-item">
            <CheckCheck size={20} />
            <span>Each QR code opens the public reporting page</span>
          </div>
          <div className="instruction-item">
            <CheckCheck size={20} />
            <span>Works with any QR scanner app</span>
          </div>
          <div className="instruction-item">
            <CheckCheck size={20} />
            <span>Print on weather-resistant material for outdoor use</span>
          </div>
        </div>
      </div>
    </div>
  ), 'QRGenerator Render');
};

export default QRGenerator;
